import axios, { AxiosRequestConfig } from 'axios';
import { useQuery } from 'react-query';
import { LoopbackFilter } from 'redux-rest-helper-for-loopback';
import { Country } from '../../classes/Country';
import apis from '../../configs/apis';
import OrderQuery from '../../store/order/OrdersQuery';

const generateHeader = (filter: LoopbackFilter): AxiosRequestConfig => ({
  params: {
    where: filter.where,
  },
});

interface DealClosingStats {
  accepted: number;
  total: number;
  percentage: number;
}

export const useDealClosingStats = () => {
  return useQuery(
    'dealClosingStats',
    async (): Promise<DealClosingStats> => {
      try {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayFilter = { tellerDate: { gt: todayStart } };

        const [acceptedRes, totalRes] = await Promise.all([
          axios.get(`${apis.ORDER}/count`, {
            params: { where: { isTellered: true, offerAccepted: true, ...todayFilter } },
          }),
          axios.get(`${apis.ORDER}/count`, {
            params: { where: { isTellered: true, ...todayFilter } },
          }),
        ]);
        const accepted = acceptedRes.data.count;
        const total = totalRes.data.count;
        const percentage = total > 0 ? Math.round((accepted / total) * 100) : 0;
        return { accepted, total, percentage };
      } catch {
        return { accepted: 0, total: 0, percentage: 0 };
      }
    }
  );
};

export const useUnvaluatedCount = (country: Country) => {
  return useQuery(`tellerUnvaluatedCount:${country}`, async () => {
    const { data } = await axios.get(
      `${apis.ORDER}/count`,
      generateHeader(OrderQuery.getUnvaluatedQuery(country))
    );
    return data;
  });
};

interface PreviousOrdersData {
  total: number;
  valuedCount: number;
  boughtCount: number;
}

export const usePreviousOrdersCount = (id: string) => {
  return useQuery(
    `previousOrdersCount:${id}`,
    async (): Promise<PreviousOrdersData> => {
      try {
        const { data: order } = await axios.get(`${apis.ORDER}/${id}`, {
          params: { filter: { fields: 'clientId' } },
        });

        const [totalRes, valuedRes, boughtRes] = await Promise.all([
          axios.get(`${apis.ORDER}/count`, {
            params: { where: { clientId: order.clientId } },
          }),
          axios.get(`${apis.ORDER}/count`, {
            params: { where: { clientId: order.clientId, isValued: true } },
          }),
          axios.get(`${apis.ORDER}/count`, {
            params: { where: { clientId: order.clientId, isPaid: true } },
          }),
        ]);

        return {
          total: totalRes.data.count,
          valuedCount: valuedRes.data.count,
          boughtCount: boughtRes.data.count,
        };
      } catch {
        return { total: 0, valuedCount: 0, boughtCount: 0 };
      }
    }
  );
};
